// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as React from "react";
import { connect } from "react-redux";

// <XNG-Changes>
interface OwnProps {
    categoryProp?: Category;
    maxNumberOfCategories?: number;
}
// </XNG-Changes>

type Props = WidgetProps & HasCategoryContext & OwnProps;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({
    // XNG-Changes: rename to contextCategory
    category: contextCategory,
    categoryProp,
    maxNumberOfCategories,
}: Props) => {
    // XNG-Changes: detrmine which category to use
    const category = categoryProp || contextCategory;
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    // <XNG-Changes>
    const subCategoryIds = maxNumberOfCategories
        ? category.subCategoryIds.slice(0, maxNumberOfCategories)
        : category.subCategoryIds;
    // </XNG-Changes>

    return (
        <GridContainer {...styles.container}>
            {subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId.toString()} {...styles.subCategoryNameLinkItem}>
                    <SubCategoryLink subCategoryId={subCategoryId} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string }) => {
    return {
        category: getCategoryState(state, ownProps.subCategoryId).value,
    };
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            data-test-selector={`categoryDetailsLinkListLink_${category.id}`}
            aria-label={category.shortDescription}
        >
            {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
